import { useState, useEffect } from 'react';
import { plusSignSVG, saveSVG, trashSVG } from '../../../lib/allCustomSVGs';
import '../../../styles/pages/settings/settingsShippingAdd.scss';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import SquareGrayButton from '../../../ui/buttons/SquareGrayButton';
import NormalInput from '../../../ui/inputs/NormalInput';
import { AnimatePresence, motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import store, { RootState } from '../../../store/store';
import { useTranslation } from 'react-i18next';
import { applicationWWWXRLEncodedBodyBuilder } from '../../../lib/utils/body-builder';
import { asyncHandler } from '../../../lib/api/async-handler';
import { RequestInfo } from '../../../interfaces/RequestInfo';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import { setMessage } from '../../../store/reducers/appSlice';
import MaskedCurrencyInput from '../../../ui/inputs/CurrencyInput';
import createSelectOptionValueObject from '../../../lib/helper-functions/createSelectOptionValueObject';
import getAllCountryNamesArray from '../../../lib/helper-functions/getAllCountryNamesArray';
import { SHIPPING_API, SHOP_API } from '../../../lib/constants/central-api';
import { submitLogToBugsnag } from '../../../lib/api/log';
import getCountryCodeByCountryName from '../../../lib/helper-functions/getCountryCodeByCountryName';

interface CountryShippingCostInterface {
  id: number;
  country: string;
  rateType: 'percentage' | 'flat' | string;
  cost: string;
  savedOnServerFlag?: boolean;
}

interface Props {
  countriesShippingCost: CountryShippingCostInterface[] | any[];
  setCountriesShippingCost: (
    input: CountryShippingCostInterface[] | any[]
  ) => void;
}

const SettingsShippingAdd = ({
  countriesShippingCost,
  setCountriesShippingCost,
}: Props) => {
  const { t } = useTranslation();
  const countriesObjFromStore = useSelector(
    (state: RootState) => state.app.countries.available
  );

  const accessToken = store.getState().auth.authorization.accessToken;
  const isWhiteLabel = store.getState().UI?.isWhiteLabel;
  const slug = store.getState().user?.company?.shop_slug;

  let sellerToken: string;
  if (isWhiteLabel) {
    sellerToken = accessToken?.currentWhiteLabelSellerToken;
  } else {
    sellerToken = accessToken?.sellerToken;
  }

  const selectOptionValues = createSelectOptionValueObject(
    getAllCountryNamesArray(countriesObjFromStore)
  );
  const [isLoading, setIsLoading] = useState(false);

  const shippingTypes = [
    { OptionValue: 'null', OptionName: `${t('Select Rate Type')}` },
    { OptionValue: 'percentage', OptionName: `${t('Percentage')}` },
    { OptionValue: 'flat', OptionName: `${t('Flat')}` },
  ];

  selectOptionValues.push({
    OptionValue: 'null',
    OptionName: 'Select Country',
  });

  useEffect(() => {
    if (slug && slug !== '') {
      let shippingRates: any = [];
      setIsLoading(true);
      const data = getShippingRate();
      data
        .then((result) => {
          if (result.length > 0) {
            for (let i of result) {
              let rate = {
                id: i.id,
                serverId: i.id,
                country: i.zone_level,
                rateType: i.rate_type,
                cost: i.rate,
                savedOnServerFlag: true,
              };
              shippingRates.push(rate);
            }
            setCountriesShippingCost(shippingRates);
          }
          setIsLoading(false);
        })
        .catch((err) => {
          console.error(err);
          setIsLoading(false);
          submitLogToBugsnag('error', `${err}`);
        });
    }
  }, [slug]);

  const addBtnHandler = () => {
    if (countriesShippingCost.length === 0) {
      setCountriesShippingCost([
        { id: 0, country: '', cost: '', rateType: '' },
      ]);
    } else {
      setCountriesShippingCost([
        ...countriesShippingCost,
        {
          id: +countriesShippingCost?.length,
          country: '',
          cost: '',
          rateType: '',
        },
      ]);
    }
  };

  const deleteBtnHandler = (item: any) => {
    if (countriesShippingCost.length === 0) {
      return;
    } else if (item.savedOnServerFlag) {
      const updatedItemArr = countriesShippingCost.filter(
        (obj) => obj?.serverId !== item?.serverId
      );
      setCountriesShippingCost(updatedItemArr);
    } else {
      const updatedItemArr = countriesShippingCost.slice(0, -1);
      setCountriesShippingCost(updatedItemArr);
    }
  };

  /* ----------------------------API CALL START---------------------------- */
  async function addShippingRate(e: any) {
    try {
      if (!e?.rateType || e?.rateType === '' || e?.rateType === 'null') {
        store.dispatch(
          setMessage({
            message: `${t('EmptyRateType')}`,
            messageType: 'error',
          })
        );
      } else if (
        e?.rateType === 'percentage' &&
        e?.cost &&
        (Number.isNaN(+e?.cost) ||
          Number(e?.cost) < 0 ||
          Number(e?.cost) > 100 ||
          Number(e?.cost) === -0)
      ) {
        store.dispatch(
          setMessage({
            message: `${t('Percentage-validation')}`,
            messageType: 'error',
          })
        );
      } else {
        const requestInfo: RequestInfo = {
          url: SHOP_API + `/${slug}/shipping`,
          method: 'POST',
          body: applicationWWWXRLEncodedBodyBuilder({
            country_code: getCountryCodeByCountryName(e?.country),
            zone_level: e?.country,
            rate_type: e?.rateType,
            rate: e?.cost,
          }),
          headers: {
            Accept: 'application/json',
            'Content-type': 'application/x-www-form-urlencoded',
            Authorization: accessToken.type + ' ' + sellerToken,
          },
        };

        const actionType = 'ADD_SHIPPING_RATE';
        const customMsg = t('ShippingRateAddedSuccess-msg');

        const res: any = await asyncHandler(requestInfo, actionType, customMsg);

        const response = res.data;

        if (res.actionType === actionType && response.success) {
          const updatedItemArr = countriesShippingCost;
          updatedItemArr[e?.id] = {
            id: response?.data?.id,
            serverId: response?.data?.id,
            country: e?.country,
            cost: e?.cost,
            rateType: e?.rateType,
            savedOnServerFlag: true,
          };
          setCountriesShippingCost(updatedItemArr);
        }
      }
    } catch (exception) {
      console.log('Error during request');
      submitLogToBugsnag('error', `${exception}`);
    }
  }
  async function deleteShippingRate(e: any) {
    try {
      const requestInfo: RequestInfo = {
        url: `${SHIPPING_API}/${e?.serverId}`,
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
          Authorization: accessToken.type + ' ' + sellerToken,
        },
      };

      const actionType = 'DELETE_SHIPPING_RATE';
      const customMsg = t('ShippingRateDeletedSuccess-msg');

      const res: any = await asyncHandler(requestInfo, actionType, customMsg);

      const response = res.data;

      if (res.actionType === actionType && response === null) {
        deleteBtnHandler(e);
      }
    } catch (exception) {
      console.log('Error during request');
      submitLogToBugsnag('error', `${exception}`);
    }
  }
  async function updateShippingRate(e: any) {
    try {
      if (!e?.rateType || e?.rateType === '' || e?.rateType === 'null') {
        store.dispatch(
          setMessage({
            message: `${t('EmptyRateType')}`,
            messageType: 'error',
          })
        );
      } else if (
        e?.rateType === 'percentage' &&
        e?.cost &&
        (Number.isNaN(+e?.cost) ||
          Number(e?.cost) < 0 ||
          Number(e?.cost) > 100 ||
          Number(e?.cost) === -0)
      ) {
        store.dispatch(
          setMessage({
            message: `${t('Percentage-validation')}`,
            messageType: 'error',
          })
        );
      } else {
        const requestInfo: RequestInfo = {
          url: `${SHIPPING_API}/${e?.serverId}`,
          method: 'PUT',
          body: applicationWWWXRLEncodedBodyBuilder({
            country_code: getCountryCodeByCountryName(e?.country),
            zone_level: e?.country,
            rate_type: e?.rateType,
            rate: e?.cost,
          }),
          headers: {
            Accept: 'application/json',
            'Content-type': 'application/x-www-form-urlencoded',
            Authorization: accessToken.type + ' ' + sellerToken,
          },
        };

        const actionType = 'UPDATE_SHIPPING_RATE';
        const customMsg = t('ShippingRateUpdatedSuccess-msg');

        const res: any = await asyncHandler(requestInfo, actionType, customMsg);

        const response = res.data;

        if (res.actionType === actionType && response.success) {
          const updatedItemArr = countriesShippingCost.map((item) => {
            if (item.serverId === e?.serverId) {
              return {
                id: e?.serverId,
                serverId: e?.serverId,
                country: e?.country,
                cost: e?.cost,
                rateType: e?.rateType,
                savedOnServerFlag: true,
              };
            }
            return item;
          });
          setCountriesShippingCost(updatedItemArr);
        }
      }
    } catch (exception) {
      console.log('Error during request');
      submitLogToBugsnag('error', `${exception}`);
    }
  }
  async function getShippingRate() {
    try {
      const requestInfo: RequestInfo = {
        url: SHOP_API + `/${slug}/shipping`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
          Authorization: accessToken.type + ' ' + sellerToken,
        },
      };

      const actionType = 'GET_SHIPPING_RATE';
      const customMsg = 'Shipping Rates fetched Successfully.';

      const res: any = await asyncHandler(
        requestInfo,
        actionType,
        customMsg,
        false
      );

      const response = res.data;

      if (res.actionType === actionType && response.success) {
        return response.data;
      }
    } catch (exception) {
      console.log('Error during request');
      submitLogToBugsnag('error', `${exception}`);
    }
  }
  /* ----------------------------API CALL END---------------------------- */

  const onChangeHandler = (
    value: any,
    id: any,
    inputType: 'country' | 'rateType' | 'cost'
  ) => {
    const changedInputIndex = countriesShippingCost.findIndex(
      (item) => item?.id === +id
    );
    const countriesShippingCostCopy: any = countriesShippingCost;
    if (typeof changedInputIndex === 'number') {
      if (inputType === 'country') {
        countriesShippingCostCopy[changedInputIndex].country = value;
        setCountriesShippingCost([...countriesShippingCostCopy]);
      } else if (inputType === 'rateType') {
        countriesShippingCostCopy[changedInputIndex].rateType = value;
        setCountriesShippingCost([...countriesShippingCostCopy]);
      } else {
        countriesShippingCostCopy[changedInputIndex].cost = value;
        setCountriesShippingCost([...countriesShippingCostCopy]);
      }
    }
  };

  return (
    <div className='settingsShippingAdd'>
      {isLoading && <LoadingSpinner />}
      {!isLoading && (
        <>
          <div className='settingsShippingAdd__bottom'>
            <AnimatePresence>
              {countriesShippingCost.map(
                (item: CountryShippingCostInterface, i: number) => {
                  if (item == null) {
                    return;
                  } else {
                    return (
                      <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        transition={{ type: 'spring' }}
                        key={item?.id}
                        className='settingsShippingAdd__bottom--item'
                      >
                        <div className='settingsShippingAdd__bottom--item-inputs'>
                          <div className='settingsShippingAdd__bottom--item-inputs-country'>
                            {/* ---------SELECT INPUT START---------- */}
                            <div className='normalSelectInput-outer'>
                              <div className='normalSelectInput'>
                                <label htmlFor={item?.id.toString()}>
                                  {t('Country')}
                                </label>
                                <select
                                  className='w-full peer border border-solid border-[#dbdbdb] rounded-lg  p-2 pb-1 outline-none focus:outline-none focus:border-[#cecece] transition-all duration-500 hover:border-[#cecece]'
                                  onChange={(e) => {
                                    onChangeHandler(
                                      e?.target?.value,
                                      e?.target?.id,
                                      'country'
                                    );
                                  }}
                                  defaultValue={
                                    item.country && item.country !== ''
                                      ? item.country
                                      : 'null'
                                  }
                                  id={item?.id.toString()}
                                  disabled={item.savedOnServerFlag}
                                >
                                  {selectOptionValues.map((selectOption, i) => {
                                    return (
                                      <option
                                        key={i}
                                        value={selectOption.OptionValue}
                                        disabled={countriesShippingCost.find(
                                          (item) =>
                                            item?.country ===
                                            selectOption?.OptionValue
                                        )}
                                      >
                                        {selectOption.OptionName}
                                      </option>
                                    );
                                  })}
                                </select>
                              </div>
                              <div className='normalSelectInput-validation-text'></div>
                            </div>
                            {/* ---------SELECT INPUT END------------ */}
                          </div>

                          <div className='settingsShippingAdd__bottom--item-inputs-country'>
                            {/* ---------SELECT INPUT START---------- */}
                            <div className='normalSelectInput-outer'>
                              <div className='normalSelectInput'>
                                <label htmlFor={item?.id.toString()}>
                                  {t('RateType')}
                                </label>
                                <select
                                  className='w-full peer border border-solid border-[#dbdbdb] rounded-lg  p-2 pb-1 outline-none focus:outline-none focus:border-[#cecece] transition-all duration-500 hover:border-[#cecece]'
                                  onChange={(e) => {
                                    onChangeHandler(
                                      e?.target?.value,
                                      e?.target?.id,
                                      'rateType'
                                    );
                                  }}
                                  defaultValue={
                                    item.rateType && item.rateType !== ''
                                      ? item.rateType
                                      : 'null'
                                  }
                                  id={item?.id.toString()}
                                >
                                  {shippingTypes.map((selectOption, i) => {
                                    return (
                                      <option
                                        key={i}
                                        value={selectOption.OptionValue}
                                      >
                                        {selectOption.OptionName}
                                      </option>
                                    );
                                  })}
                                </select>
                              </div>
                              <div className='normalSelectInput-validation-text'></div>
                            </div>
                            {/* ---------SELECT INPUT END------------ */}
                          </div>

                          <div className='settingsShippingAdd__bottom--item-inputs-cost'>
                            {
                              item.rateType && item.rateType === 'percentage' && (
                                // ---------NORMAL INPUT START----------
                                <NormalInput
                                  changeListeners={[
                                    (e) =>
                                      onChangeHandler(
                                        e?.target?.value,
                                        e?.target?.id,
                                        'cost'
                                      ),
                                  ]}
                                  id={item?.id.toString()}
                                  value={item?.cost ?? 0.0}
                                  placeholder={t('Value')}
                                  required={true}
                                  pattern='^100(\.0{0,2})? *%?$|^[0-9]{1,2}(\.[0-9]{1,2})? *%?$'
                                  title={t('Percentage-validation')}
                                />
                              )
                              // ---------NORMAL INPUT END------------
                            }{' '}
                            {
                              item.rateType && item.rateType === 'flat' && (
                                // ---------CURRENCY INPUT START----------
                                <MaskedCurrencyInput
                                  changeListeners={[
                                    (value: any) =>
                                      onChangeHandler(value, item?.id, 'cost'),
                                  ]}
                                  id={item?.id.toString()}
                                  value={
                                    item?.cost && !Number.isNaN(item?.cost)
                                      ? item?.cost
                                      : '0'
                                  }
                                  placeholder={t('Value')}
                                  required={true}
                                  title={t('OnlyNumbers-validation')}
                                  type='text'
                                  pattern='^[\d.,]+/'
                                  defaultValue={
                                    item?.cost && !Number.isNaN(item?.cost)
                                      ? item?.cost
                                      : '0'
                                  }
                                />
                              )
                              // ---------CURRENCY INPUT END------------
                            }
                          </div>
                        </div>
                        <div className='settingsShippingAdd__bottom--item-btn-container'>
                          <div className='settingsShippingAdd__bottom--item-btn'>
                            <SquareGrayButton
                              onClick={(e) => {
                                e.preventDefault();
                                if (
                                  !item?.country ||
                                  item?.country === '' ||
                                  !item?.rateType ||
                                  item?.rateType === '' ||
                                  !item?.cost ||
                                  item?.cost === ''
                                ) {
                                  return;
                                } else {
                                  if (item.savedOnServerFlag) {
                                    updateShippingRate(item);
                                  } else {
                                    addShippingRate(item);
                                  }
                                }
                              }}
                              type={'button'}
                              icon={saveSVG}
                              normal={false}
                              disabled={
                                !item?.country ||
                                item?.country === '' ||
                                !item?.rateType ||
                                item?.rateType === '' ||
                                !item?.cost ||
                                item?.cost === ''
                              }
                              classes='squareGrayButton-add'
                            />
                          </div>
                          <div className='settingsShippingAdd__bottom--item-btn'>
                            <SquareGrayButton
                              onClick={(e) => {
                                e.preventDefault();
                                if (item.savedOnServerFlag) {
                                  deleteShippingRate(item);
                                } else {
                                  deleteBtnHandler(item);
                                }
                              }}
                              type={'button'}
                              icon={trashSVG}
                              normal={false}
                              /* value={(item + 1).toString()} */
                              classes='squareGrayButton-delete'
                            />
                          </div>
                        </div>
                      </motion.div>
                    );
                  }
                }
              )}
            </AnimatePresence>
          </div>
          <div className='settingsShippingAdd__top'>
            <h1> </h1>
            <MainSquareButton
              onClick={(e) => {
                e.preventDefault();
                addBtnHandler();
              }}
              type={'button'}
              value={t('AddCountry')}
              icon={plusSignSVG}
              normal={true}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default SettingsShippingAdd;
